<template>
    <modal-component
        :id="'appointment-modal'"
        :visible="visible"
        :title="title"
        :size="size"
        @close="$emit('close')"
    >
        <transition name="simple-fade">
            <div class="pr-sm-3" v-if="!showCompleteAppointment && !appointmentCancellation.cancel">
                <form>
                    <!--APPOINTMENT INFO-->
                    <section>
                        <div v-if="appointment.approved_by_user" class="alert alert-success mb-2">
                            This appointment was approved by {{ appointment.approved_by_user }}.
                        </div>
                        <div class="form-group row mb-2">
                            <label
                                for="appointment-patient"
                                class="col-6 col-form-label font-weight-bold text-nowrap text-capitalize"
                                >{{ participantsListProp }}:</label
                            >
                            <select
                                id="appointment-patient"
                                data-has-watcher="true"
                                class="col-6 form-control form-control-sm"
                                :disabled="!canEditAppointment"
                                @change="handleChangedPatient"
                                v-model="appointment[participantsListProp + '_id']"
                            >
                                <option v-if="participantsListProp === 'patient'" :value="null"
                                    >None</option
                                >
                                <option
                                    v-for="p in participantsList"
                                    :disabled="!matchedParticipants.includes(p.id)"
                                    :class="!matchedParticipants.includes(p.id) ? 'text-light' : ''"
                                    :value="p.id"
                                    >{{ `${p.name}` }}</option
                                >
                            </select>
                        </div>
                        <div class="form-group row mb-2">
                            <label
                                for="appointment-type"
                                class="col-6 col-form-label font-weight-bold text-nowrap "
                                >Appointment type:</label
                            >
                            <!--PLATINUM ORGS BEGIN-->
                            <template v-if="['professional_platinum'].includes(organization.type)">
                                <select
                                    id="appointment-type"
                                    data-has-watcher="true"
                                    class="col-6 form-control form-control-sm"
                                    v-model="appointment.appointment_type_id"
                                    :disabled="!canEditAppointment"
                                >
                                    <!-- This has a watcher -->
                                    <option :value="null">Choose one</option>
                                    <optgroup label="Non-billable appointment types">
                                        <option
                                            v-for="apptType in appointmentTypes.filter(
                                                at => !at.is_billable
                                            )"
                                            :value="apptType.id"
                                            >{{ `${apptType.name}` }}</option
                                        >
                                    </optgroup>
                                    <optgroup label="Appointment types with active contracts">
                                        <option
                                            v-for="apptType in appointmentTypesWithContract"
                                            :value="apptType.id"
                                            >{{ `${apptType.name}` }}</option
                                        >
                                        <option disabled v-if="!appointmentTypesWithContract.length"
                                            >---</option
                                        >
                                    </optgroup>
                                    <optgroup label="Appointment types for private pay only">
                                        <option
                                            v-for="apptType in appointmentTypes.filter(
                                                at => !appointmentTypeIsFunded(at)
                                            )"
                                            :value="apptType.id"
                                            >{{ `${apptType.name}` }}</option
                                        >
                                    </optgroup>
                                </select>
                            </template>
                            <!--PLATINUM ORGS END-->
                            <!--GOLD/SILVER ORGS BEGIN-->
                            <template v-else="">
                                <select
                                    id="appointment-type"
                                    class="col-6 form-control form-control-sm"
                                    v-model="appointment.appointment_type_id"
                                    :disabled="!canEditAppointment"
                                >
                                    <option :value="null">Choose one</option>
                                    <option
                                        v-for="apptType in appointmentTypes"
                                        :value="apptType.id"
                                        >{{ `${apptType.name}` }}</option
                                    >
                                </select>
                            </template>

                            <!--GOLD/SILVER ORGS END-->
                        </div>
                        <div
                            class="form-group row mb-2"
                            v-show="['professional_platinum'].includes(organization.type)"
                        >
                            <label
                                for="contract"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Contract:</label
                            >
                            <select
                                id="contract"
                                class="col-6 form-control form-control-sm"
                                :disabled="
                                    (appointment.appointment_type_id &&
                                        !appointmentTypeIsBillable) ||
                                        !canEditAppointment ||
                                        (appointment.contract_id === 'private_pay' &&
                                            !appointmentTypeIsFunded(appointmentType))
                                "
                                v-model="appointment.contract_id"
                            >
                                <option
                                    hidden
                                    disabled
                                    selected
                                    :value="null"
                                    v-if="
                                        !appointment.appointment_type_id ||
                                            !appointment[participantsListProp + '_id']
                                    "
                                >
                                    Select a type of appointment and patient</option
                                >
                                <option hidden disabled selected :value="null" v-else="">
                                    Choose one</option
                                >
                                <option
                                    v-for="contract in availableContracts"
                                    :value="contract.id"
                                    >{{
                                        `${prettyType(contract.funding_source.type)} - ${
                                            contract.funding_source.name
                                        }`
                                    }}</option
                                >
                                <option v-if="patient" value="private_pay">Private pay</option>
                            </select>
                        </div>
                        <div
                            class="form-group row mb-2"
                            v-if="appointment.contract_id === 'private_pay'"
                        >
                            <label
                                for="charge_amount"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Charge amount:</label
                            >
                            <div class="col-6 p-0">
                                <div class="input-group mb-2">
                                    <div class="input-group-prepend">
                                        <button
                                            class="btn btn-sm btn-outline-dark"
                                            type="button"
                                            style="border-color: #ced4da;"
                                        >
                                            <i class="fa fa-dollar"></i>
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        id="charge_amount"
                                        class="form-control form-control-sm text-right"
                                        placeholder="0.00"
                                        v-model.number="appointment.charge_amount"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="form-group row mb-2">
                            <label
                                for="location"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Session location address:</label
                            >
                            <div class="col-6 p-0">
                                <div class="input-group mb-2">
                                    <div class="input-group-prepend">
                                        <button
                                            class="btn btn-sm btn-outline-dark dropdown-toggle"
                                            type="button"
                                            data-toggle="dropdown"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            style="border-color: #ced4da;"
                                        >
                                            <i class="fa fa-map-marker"></i>
                                        </button>
                                        <div class="dropdown-menu">
                                            <a
                                                v-for="a in availableAddresses"
                                                class="dropdown-item py-0 small cursor-pointer"
                                                @click="populateAddress(a)"
                                                >{{ `${a.address1} - ${a.city}` }}</a
                                            >
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        id="location"
                                        :disabled="!canEditAppointment"
                                        class="form-control form-control-sm"
                                        placeholder="Select existing address or enter new"
                                        v-model="appointment.address"
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            class="form-group row mb-2"
                            v-if="
                                appointment.appointment_type_id &&
                                    appointmentType.type === 'driving'
                            "
                        >
                            <label
                                for="distance"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Distance:</label
                            >
                            <div class="col-6 p-0 form-inline">
                                <input
                                    type="text"
                                    id="distance"
                                    class="form-control form-control-sm"
                                    :disabled="!canEditAppointment"
                                    v-model="appointment.distance"
                                />
                                <span class="ml-2">miles</span>
                            </div>
                        </div>
                        <div
                            class="form-group row mb-2"
                            v-if="
                                helpers._get(contract, 'funding_source.type', null) ===
                                    'insurance_provider'
                            "
                        >
                            <label
                                for="service_location_id"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Place of service:</label
                            >
                            <select
                                class="col-6 form-control form-control-sm"
                                id="service_location_id"
                                :disabled="!canEditAppointment"
                                v-model="appointment.service_location_id"
                            >
                                <option value="0"></option>
                                <option v-for="p in placesOfService" :value="p.id">{{
                                    `[${p.code}] ${p.name}`
                                }}</option>
                            </select>
                        </div>
                        <div class="form-group row mb-2">
                            <label
                                for="note"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Notes:</label
                            >
                            <textarea
                                id="note"
                                class="col-6 form-control textarea"
                                :disabled="!canEditAppointment"
                                v-model="appointment.notes"
                            ></textarea>
                        </div>
                        <div v-if="appointment.id" class="form-group row mb-2">
                            <label
                                for="data-tracked"
                                class="col-6 col-form-label font-weight-bold text-nowrap"
                                >Data tracked:</label
                            >
                            <select
                                id="data-tracked"
                                class="col-6 form-control form-control-sm"
                                :disabled="!canEditAppointment"
                                v-model="appointment.is_data_tracked"
                            >
                                <option :value="null">Choose one</option>
                                <option :value="0">No</option>
                                <option :value="1">Yes</option>
                            </select>
                        </div>
                    </section>
                    <!--CPT/SERVICE CODES AND DIAGNOSES-->
                    <section
                        v-if="
                            ['professional_platinum'].includes(organization.type) &&
                                !(
                                    appointment.contract_id === 'private_pay' ||
                                    appointment.is_private_pay
                                )
                        "
                    >
                        <div class="row mt-2">
                            <div class="col-6">
                                <label class="font-weight-bold text-nowrap">
                                    Enter
                                    {{
                                        helpers._get(contract, "funding_source.type", null) ===
                                        "insurance_provider"
                                            ? "CPT & Diagnostic"
                                            : "Service"
                                    }}
                                    Codes?
                                </label>
                            </div>

                            <div class="col-3 d-flex justify-content-start align-items-center">
                                <toggle-button
                                    :disabled="
                                        !contract ||
                                            !contract.hasOwnProperty('funding_source') ||
                                            (appointment.appointment_type_id &&
                                                !appointmentTypeIsBillable) ||
                                            !canEditAppointment
                                    "
                                    :value="showCodes"
                                    color="#D64571"
                                    @change="showCodes = $event.value"
                                    :sync="true"
                                    :labels="{ checked: 'yes', unchecked: 'no' }"
                                />
                            </div>
                        </div>

                        <template v-if="showCodes">
                            <template
                                v-if="
                                    helpers._get(contract, 'funding_source.type', null) ===
                                        'insurance_provider'
                                "
                            >
                                <div class="form-group row mb-2">
                                    <label
                                        for="report-cpt-approve"
                                        class="col-6 col-form-label font-weight-bold text-nowrap"
                                        >Choose CPT code:</label
                                    >
                                    <template v-if="patient && contract">
                                        <select
                                            class="col-6 form-control form-control-sm"
                                            id="report-cpt-approve"
                                            :disabled="!canEditAppointment"
                                            v-model="appointment.preferred_cpt_code_id"
                                        >
                                            <option v-for="c in contract.cpt_codes" :value="c.id">{{
                                                `${c.code} - ${c.service_nickname}`
                                            }}</option>
                                        </select>
                                    </template>
                                </div>

                                <div
                                    v-if="Array.isArray(appointment.diagnoses)"
                                    class="form-group row mb-2"
                                >
                                    <label
                                        for="diagnoses"
                                        class="col-6 col-form-label font-weight-bold text-nowrap"
                                        >Choose diagnoses:</label
                                    >
                                    <template v-if="patient && contract">
                                        <div class="col-6 px-0">
                                            <vw-multi-select
                                                id="diagnoses"
                                                v-model="appointment.diagnoses"
                                                :disabled="!canEditAppointment"
                                                :template="'bootstrap'"
                                                :styles="{ input: { width: '100%' } }"
                                                :iconset="'font-awesome'"
                                                :list="contract.diagnoses"
                                                :value-property="'code'"
                                                :searchable="true"
                                                :tags="true"
                                                :selected-list-display-function="item => item.code"
                                                :list-item-display-function="
                                                    item =>
                                                        `${item.code} - ${item.service_nickname}`
                                                "
                                            ></vw-multi-select>
                                        </div>
                                    </template>
                                </div>
                            </template>

                            <template v-else="">
                                <div v-if="contract" class="form-group row mb-2">
                                    <label
                                        for="report-service-approve"
                                        class="col-6 col-form-label font-weight-bold text-nowrap"
                                        >Choose Service code:</label
                                    >
                                    <template v-if="patient && contract">
                                        <select
                                            class="col-6 form-control form-control-sm"
                                            id="report-service-approve"
                                            :disabled="!canEditAppointment"
                                            v-model="appointment.service_codes"
                                        >
                                            <option
                                                v-for="c in contract.service_codes"
                                                :value="[c.code]"
                                                >{{ `${c.code} - ${c.service_nickname}` }}</option
                                            >
                                        </select>
                                    </template>
                                </div>
                            </template>
                        </template>
                    </section>

                    <!--DATE/TIME-->
                    <section
                        v-show="appointment.status !== 'complete' || canEditCompletedAppointments"
                    >
                        <div class="row">
                            <div class="col-12 col-sm-6">
                                <label
                                    class="font-weight-bold text-nowrap mr-3"
                                    @click="duskSetDate()"
                                    dusk="set-date"
                                    >Date:</label
                                >
                                <div class="d-flex justify-content-between">
                                    <v-date-picker
                                        data-has-watcher="true"
                                        class="form-control form-control-sm"
                                        :disabled="!canEditAppointment"
                                        :disabled-dates="disabledDates"
                                        v-model="appointment.startDate"
                                    ></v-date-picker>
                                </div>
                            </div>
                            <div class="col-12 col-sm-3 pr-sm-0">
                                <label class="font-weight-bold text-nowrap mr-3">Start:</label>
                                <div class="d-flex justify-content-between">
                                    <time-picker
                                        data-has-watcher="true"
                                        v-model="appointment.startTime"
                                        :disabled="!canEditAppointment"
                                        :id="'start-time'"
                                        :picker-class="'form-control form-control-sm'"
                                        :options="{ minutesInterval: 30 }"
                                        :start-hour="startHour"
                                        :end-hour="finishHour - 1"
                                    ></time-picker>
                                </div>
                            </div>
                            <div class="col-12 col-sm-3 pr-sm-0">
                                <label class="font-weight-bold text-nowrap mr-3">End:</label>
                                <div class="d-flex justify-content-between">
                                    <time-picker
                                        data-has-watcher="true"
                                        v-model="appointment.endTime"
                                        :disabled="!canEditAppointment"
                                        :id="'end-time'"
                                        :options="{ minutesInterval: 30 }"
                                        :start-hour="
                                            appointment.startTime
                                                ? moment(appointment.startTime, 'hh:mm A').hours()
                                                : null
                                        "
                                        :start-minute="
                                            appointment.startTime
                                                ? moment(appointment.startTime, 'hh:mm A').minutes()
                                                : null
                                        "
                                        :picker-class="'form-control form-control-sm'"
                                    ></time-picker>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section
                        v-show="appointment.status === 'complete' && !canEditCompletedAppointments"
                    >
                        <div class="row">
                            <div class="col-12 col-sm-6">
                                <label
                                    class="font-weight-bold text-nowrap mr-3"
                                    @click="duskSetDate()"
                                    dusk="set-date"
                                    >Date:</label
                                >
                                <div class="d-flex justify-content-between">
                                    <input
                                        disabled="disabled"
                                        class="form-control form-control-sm"
                                        type="text"
                                        :value="
                                            moment(appointment.time_start_at).format('MM/DD/YYYY')
                                        "
                                    />
                                </div>
                            </div>
                            <div class="col-12 col-sm-3 pr-sm-0">
                                <label class="font-weight-bold text-nowrap mr-3">Start:</label>
                                <div class="d-flex justify-content-between">
                                    <input
                                        disabled="disabled"
                                        class="form-control form-control-sm"
                                        type="text"
                                        :value="moment(appointment.time_start_at).format('hh:mm A')"
                                    />
                                </div>
                            </div>
                            <div class="col-12 col-sm-3 pr-sm-0">
                                <label class="font-weight-bold text-nowrap mr-3">End:</label>
                                <div class="d-flex justify-content-between">
                                    <input
                                        disabled="disabled"
                                        class="form-control form-control-sm"
                                        type="text"
                                        :value="moment(appointment.time_end_at).format('hh:mm A')"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                    <!--RECURRENCE-->
                    <section v-show="appointment.status !== 'complete'">
                        <div class="row mt-2">
                            <div class="col-6">
                                <label class="font-weight-bold text-nowrap">Repeats:</label>
                            </div>

                            <div class="col-3 d-flex justify-content-start align-items-center">
                                <toggle-button
                                    :value="!!recurring"
                                    :disabled="!canEditAppointment"
                                    color="#D64571"
                                    @change="recurring = $event.value"
                                    :sync="true"
                                    :labels="true"
                                />
                            </div>
                        </div>
                        <div v-show="recurring">
                            <div class="row">
                                <div class="col-4">
                                    <select
                                        id="appointment-frequency"
                                        class="form-control form-control-sm"
                                        @change="setFrequency($event.target.value)"
                                        :disabled="!canEditAppointment"
                                        v-model="frequency"
                                    >
                                        <option hidden disabled selected :value="null"
                                            >Select one</option
                                        >
                                        <option v-for="f in frequencyOptions" :value="f.value">{{
                                            f.pretty
                                        }}</option>
                                    </select>
                                </div>
                                <div class="col-1 d-flex justify-content-center">
                                    <span>on</span>
                                </div>
                                <div class="col-7">
                                    <div class="form-group d-flex justify-content-start">
                                        <span
                                            v-for="(d, i) in ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']"
                                            :class="
                                                selectedDays[i] ? 'selected-day' : 'unselected-day'
                                            "
                                            @click.prevent="
                                                canEditAppointment
                                                    ? $set(selectedDays, i, !selectedDays[i])
                                                    : null
                                            "
                                            >{{ d }}</span
                                        >
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-2">
                                    <label class="font-weight-bold text-nowrap mr-3">Until:</label>
                                </div>
                                <div
                                    class="col-6"
                                    v-show="
                                        ['professional_platinum'].includes(organization.type) &&
                                            contract &&
                                            contract.id
                                    "
                                >
                                    <select
                                        id="recurrence-end"
                                        class="form-control form-control-sm"
                                        :disabled="!canEditAppointment"
                                        v-model="recurrenceEndCustom"
                                    >
                                        <option :value="false"
                                            >Contract end
                                            {{
                                                selectedContractEndDate
                                                    ? `(${moment(selectedContractEndDate).format(
                                                          "MM-DD-YY"
                                                      )})`
                                                    : ""
                                            }}</option
                                        >
                                        <option :value="true">Custom</option>
                                    </select>
                                </div>
                                <div
                                    v-show="
                                        recurrenceEndCustom ||
                                            !['professional_platinum'].includes(
                                                organization.type
                                            ) ||
                                            !helpers._get(contract, 'id', false)
                                    "
                                    class="col-4"
                                >
                                    <v-date-picker
                                        class="form-control form-control-sm"
                                        :disabled="!canEditAppointment"
                                        v-model="appointment.recurrence.end_at"
                                    ></v-date-picker>
                                </div>
                            </div>
                        </div>
                    </section>
                    <!--BREAK TIME-->
                    <section
                        v-show="appointment.status !== 'complete' || canEditCompletedAppointments"
                    >
                        <div class="row mt-2">
                            <div class="col-3">
                                <label class="font-weight-bold text-nowrap">Break time:</label>
                            </div>
                            <div class="col-3 d-flex justify-content-start align-items-center">
                                <toggle-button
                                    :value="!!breakTime.enabled"
                                    :disabled="
                                        (appointment.appointment_type_id &&
                                            !appointmentTypeIsBillable) ||
                                            !canEditAppointment
                                    "
                                    color="#D64571"
                                    @change="breakTime.enabled = $event.value"
                                    :sync="true"
                                    :labels="true"
                                />
                            </div>
                            <div v-show="breakTime.enabled" class="col-3 pr-0">
                                <time-picker
                                    v-model="breakTime.startTime"
                                    :disabled="!canEditAppointment"
                                    :options="{ minutesInterval: 30 }"
                                    :start-hour="
                                        appointment.startTime
                                            ? appointment.startTime.split(':')[0]
                                            : null
                                    "
                                    :start-minute="
                                        appointment.startTime
                                            ? appointment.startTime.split(':')[1]
                                            : null
                                    "
                                    :end-hour="
                                        appointment.endTime
                                            ? appointment.endTime.split(':')[0]
                                            : null
                                    "
                                    :end-minute="
                                        appointment.endTime
                                            ? appointment.endTime.split(':')[1]
                                            : null
                                    "
                                    :picker-class="'form-control form-control-sm'"
                                ></time-picker>
                            </div>
                            <div v-show="breakTime.enabled" class="col-3">
                                <select
                                    name="break-duration"
                                    id="break-duration"
                                    class="form-control form-control-sm"
                                    :disabled="!canEditAppointment"
                                    v-model="breakTime.duration"
                                >
                                    <option hidden disabled selected :value="null">Duration</option>
                                    <option v-for="t in 11" :value="t * 5"
                                        >{{ t * 5 }} minutes</option
                                    >
                                    <option :value="60">1 hour</option>
                                </select>
                            </div>
                        </div>
                    </section>
                    <section
                        v-show="appointment.status === 'complete' && !canEditCompletedAppointments"
                    >
                        <div class="row mt-2" v-if="appointment.break_time_start_at">
                            <div class="col-3">
                                <label class="font-weight-bold text-nowrap">Break time:</label>
                            </div>
                            <div
                                class="col-3 d-flex justify-content-start align-items-center"
                            ></div>
                            <div class="col-3">
                                <input
                                    disabled="disabled"
                                    class="form-control form-control-sm"
                                    type="text"
                                    :value="
                                        moment(appointment.break_time_start_at).format('hh:mm A')
                                    "
                                />
                            </div>
                            <div class="col-3">
                                <input
                                    disabled="disabled"
                                    class="form-control form-control-sm"
                                    type="text"
                                    :value="moment(appointment.break_time_end_at).format('hh:mm A')"
                                />
                            </div>
                        </div>
                    </section>
                    <!--LUNCH TIME-->
                    <section
                        v-show="appointment.status !== 'complete' || canEditCompletedAppointments"
                    >
                        <div class="row mt-2">
                            <div class="col-3">
                                <label class="font-weight-bold text-nowrap">Lunch time:</label>
                            </div>
                            <div class="col-3 d-flex justify-content-start align-items-center">
                                <toggle-button
                                    :value="!!lunchTime.enabled"
                                    :disabled="
                                        (appointment.appointment_type_id &&
                                            !appointmentTypeIsBillable) ||
                                            !canEditAppointment
                                    "
                                    color="#D64571"
                                    @change="lunchTime.enabled = $event.value"
                                    :sync="true"
                                    :labels="true"
                                />
                            </div>
                            <div v-show="lunchTime.enabled" class="col-3 pr-0">
                                <time-picker
                                    v-model="lunchTime.startTime"
                                    :disabled="!canEditAppointment"
                                    :options="{ minutesInterval: 30 }"
                                    :start-hour="
                                        appointment.startTime
                                            ? appointment.startTime.split(':')[0]
                                            : null
                                    "
                                    :start-minute="
                                        appointment.startTime
                                            ? appointment.startTime.split(':')[1]
                                            : null
                                    "
                                    :end-hour="
                                        appointment.endTime
                                            ? appointment.endTime.split(':')[0]
                                            : null
                                    "
                                    :end-minute="
                                        appointment.endTime
                                            ? appointment.endTime.split(':')[1]
                                            : null
                                    "
                                    :picker-class="'form-control form-control-sm'"
                                ></time-picker>
                            </div>
                            <div v-show="lunchTime.enabled" class="col-3">
                                <select
                                    name="lunch-duration"
                                    id="lunch-duration"
                                    class="form-control form-control-sm"
                                    :disabled="!canEditAppointment"
                                    v-model="lunchTime.duration"
                                >
                                    <option hidden disabled selected :value="null">Duration</option>
                                    <option v-for="t in 11" :value="t * 5"
                                        >{{ t * 5 }} minutes</option
                                    >
                                    <option :value="60">1 hour</option>
                                </select>
                            </div>
                        </div>
                    </section>
                    <section
                        v-show="appointment.status === 'complete' && !canEditCompletedAppointments"
                    >
                        <div class="row mt-2" v-if="appointment.lunch_time_start_at">
                            <div class="col-3">
                                <label class="font-weight-bold text-nowrap">Lunch time:</label>
                            </div>
                            <div
                                class="col-3 d-flex justify-content-start align-items-center"
                            ></div>
                            <div class="col-3">
                                <input
                                    disabled="disabled"
                                    class="form-control form-control-sm"
                                    type="text"
                                    :value="
                                        moment(appointment.lunch_time_start_at).format('hh:mm A')
                                    "
                                />
                            </div>
                            <div class="col-3">
                                <input
                                    disabled="disabled"
                                    class="form-control form-control-sm"
                                    type="text"
                                    :value="moment(appointment.lunch_time_end_at).format('hh:mm A')"
                                />
                            </div>
                        </div>
                    </section>
                    <div class="alert alert-warning mt-3" v-if="contractValidationMessage">
                        {{ contractValidationMessage }}
                    </div>
                    <div
                        v-if="canEditCompletedAppointments || appointment.status !== 'complete'"
                        class="w-100 d-flex justify-content-end mt-3"
                    >
                        <template v-if="appointment.id">
                            <button
                                class="btn btn-sm btn-outline-danger font-weight-bold text-uppercase mr-4"
                                id="delete-appointment"
                                @click.prevent="confirmDeleteAppointment()"
                            >
                                Delete
                            </button>
                            <button
                                :disabled="
                                    appointmentIsDirty ||
                                        (!(appointment.contract_id || !appointmentTypeIsBillable) &&
                                            !['professional_gold'].includes(organization.type))
                                "
                                v-tooltip="
                                    appointmentIsDirty
                                        ? 'Please save your changes before completing the appointment'
                                        : null
                                "
                                class="btn btn-sm btn-outline-primary font-weight-bold text-uppercase mr-4"
                                id="complete-appointment"
                                @click.prevent="handleCompleteAppointment"
                                v-if="canCompleteAppointment"
                            >
                                <template>
                                    <i
                                        v-if="appointmentIsDirty"
                                        class="fa fa-exclamation-triangle mr-2"
                                    ></i>
                                    <span>{{
                                        isSafari && appointmentIsDirty
                                            ? "Unsaved changes"
                                            : "Complete Appointment"
                                    }}</span>
                                </template>
                            </button>
                        </template>
                        <template v-if="isPartOfRecurringSeries">
                            <div class="dropdown">
                                <button
                                    class="btn btn-sm btn-primary dropdown-toggle mr-4"
                                    type="button"
                                    id="save-recurring"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    :disabled="!canEditAppointment"
                                >
                                    Save...
                                </button>
                                <div class="dropdown-menu" aria-labelledby="delete-recurring">
                                    <a
                                        class="cursor-pointer dropdown-item small"
                                        @click="saveAppointment(true)"
                                        >Only this appointment</a
                                    >
                                    <a
                                        class="cursor-pointer dropdown-item small"
                                        @click="saveAppointment(false)"
                                        >This and future appointments</a
                                    >
                                </div>
                            </div>
                        </template>
                        <button
                            v-else=""
                            class="btn btn-sm btn-primary font-weight-bold text-uppercase text-white"
                            id="save-appointment"
                            :disabled="!canEditAppointment"
                            @click.prevent="saveAppointment()"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
            <div class="px-sm-3" v-else-if="appointmentCancellation.cancel">
                <div class="form-group row mb-2 pr-3">
                    <label
                        for="cancelation-reason-select"
                        class="col-6 col-form-label font-weight-bold text-nowrap py-0"
                        >Cancellation reason:</label
                    >
                    <select
                        id="cancelation-reason-select"
                        class="col-6 form-control form-control-sm"
                        v-model="appointmentCancellation.reason"
                    >
                        <option disabled hidden selected :value="null">Choose one</option>
                        <option>Client called off sick</option>
                        <option>Staff called off sick</option>
                        <option>Planned cancellation</option>
                        <option>Unplanned cancellation</option>
                        <option :value="'other'">Other (please specify)</option>
                    </select>
                </div>
                <div
                    v-if="appointmentCancellation.reason === 'other'"
                    class="form-group row mb-2 pr-3"
                >
                    <label
                        for="cancellation-reason-specify"
                        class="col-6 col-form-label font-weight-bold text-nowrap"
                        >Notes:</label
                    >
                    <textarea
                        id="cancellation-reason-specify"
                        class="col-6 form-control textarea"
                        v-model="appointmentCancellation.specify"
                    ></textarea>
                    <div
                        class="text-right font-italic w-100 d-flex justify-content-end align-items-center small"
                        v-if="appointmentCancellation.specify.length > 255"
                    >
                        <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
                        Reason can't be longer than 255 characters
                    </div>
                </div>
                <errors :errors="errors"></errors>
                <div class="w-100 d-flex justify-content-end align-items-center">
                    <template v-if="isPartOfRecurringSeries && appointment.status !== 'complete'">
                        <div class="dropdown">
                            <button
                                :disabled="
                                    appointmentCancellation.specify.length > 255 ||
                                        !appointmentCancellation.reason ||
                                        (appointmentCancellation.reason === 'other' &&
                                            !appointmentCancellation.specify)
                                "
                                class="btn btn-sm btn-outline-danger dropdown-toggle"
                                type="button"
                                id="delete-recurring-confirm"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                Delete...
                            </button>
                            <div class="dropdown-menu" aria-labelledby="delete-recurring">
                                <a
                                    class="cursor-pointer dropdown-item text-danger small"
                                    @click="deleteAppointment()"
                                    >Only this appointment</a
                                >
                                <a
                                    class="cursor-pointer dropdown-item text-danger small"
                                    @click="
                                        deleteAppointment({
                                            delete_recurrences: true,
                                            delete_forwards: true,
                                        })
                                    "
                                    >This and future appointments</a
                                >
                                <a
                                    class="cursor-pointer dropdown-item text-danger small"
                                    @click="deleteAppointment({ delete_recurrences: true })"
                                    >Every non-complete appointment</a
                                >
                            </div>
                        </div>
                    </template>
                    <template v-else="">
                        <button
                            :disabled="
                                !appointmentCancellation.reason ||
                                    (appointmentCancellation.reason === 'other' &&
                                        !appointmentCancellation.specify)
                            "
                            class="btn btn-sm btn-outline-danger font-weight-bold text-uppercase"
                            id="delete-appointment-confirm"
                            @click.prevent="deleteAppointment()"
                        >
                            Delete
                        </button>
                    </template>
                </div>
            </div>
            <div class="px-sm-3" v-else="">
                <session-confirmation
                    :user="user"
                    :current-user-therapist="currentUserTherapist"
                    :organization="organization"
                    :schedule-owner-prop="scheduleOwnerProp"
                    :supervisors="supervisors"
                    :appointment="appointment"
                    :scheduleOwner="scheduleOwner"
                    :appointment-type="appointmentType"
                    :contract="contract"
                    :participant="participant"
                    @close-confirmation="closeCompleteAppointment()"
                ></session-confirmation>
            </div>
        </transition>
    </modal-component>
</template>

<script>
import TimePicker from "../ui/TimePicker";
import ModalComponent from "../ui/ModalComponent";
import SessionConfirmation from "./SessionConfirmation";
import { helpers } from "../../plugins/helpers";
import { fixDatePickers } from "../../plugins/fixDatePickers";
import { permissions } from "../../plugins/permissions";
import { changeDetection } from "./changeDetection";
import { errors } from "../../plugins/errors";

export default {
    name: "ScheduleNewAppointment",
    mixins: [changeDetection, fixDatePickers, permissions, errors],
    components: {
        TimePicker,
        ModalComponent,
        SessionConfirmation,
    },
    props: [
        "visible",
        "title",
        "size",
        "organization",
        "organizationTherapists",
        "supervisors",
        "user",
        "centers",
        "currentUserTherapist",
        "appointmentTypes",
        "appointmentTemplates",
        "scheduleOwner",
        "scheduleOwnerProp",
        "scheduleOwnerList",
        "participantsList",
        "participantsListProp",
        "therapistPatientRelations",
        "placesOfService",
        "blackoutDates",
    ],
    data() {
        return {
            startHour: 6,
            finishHour: 22,
            helpers,
            appointmentCancellation: {
                cancel: false,
                reason: null,
                specify: "",
            },
            appointment: {
                approved_by_user_id: null,
                appointment_type_id: null,
                patient_id: null,
                therapist_id: null,
                contract_id: null,
                charge_amount: null,
                is_private_pay: 0,
                time_start_at: null,
                time_end_at: null,
                startTime: null,
                endTime: null,
                diagnoses: [],
                cpt_codes: null,
                service_codes: null,
                preferred_cpt_code_id: null,
                service_location_id: null,
                recurrence: {
                    days_of_week: [],
                    frequency: null,
                    interval: null,
                    end_at: null,
                },
            },
            appointmentClean: {},
            breakTimeClean: {},
            lunchTimeClean: {},
            recurrenceClean: {},
            selectedDays: [false, false, false, false, false, false],
            frequencyOptions: [
                { pretty: "Every week", value: "WEEKLY", frequency: "WEEKLY", interval: 1 },
                { pretty: "Every 2 weeks", value: "WEEKLY2", frequency: "WEEKLY", interval: 2 },
                { pretty: "Every month", value: "MONTHLY", frequency: "MONTHLY", interval: 1 },
            ],
            appointmentCpt: {
                cpt_code_id: null,
                amount: null,
                minutes: null,
            },
            recurring: false,
            breakTime: {
                enabled: false,
                startTime: null,
                duration: null,
            },
            lunchTime: {
                enabled: false,
                startTime: null,
                duration: null,
            },
            showCompleteAppointment: false,
            locations: [],
            recurrenceEndCustom: true,
            frequency: "WEEKLY",

            showCodes: false,
            contractValidationMessage: "",
            isSafari: false,
        };
    },

    mounted() {
        this.isSafari =
            (!!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) ||
            navigator.userAgent.indexOf("Safari") > -1;
    },

    watch: {
        visible() {
            this.contractValidationMessage = "";
        },
        "appointment.startDate"() {
            if (this.appointment.startDate) {
                if (!this.selectedDays.filter(s => s).length) {
                    const dow = parseInt(this.moment(this.appointment.startDate).format("d"));
                    if ([1, 2, 3, 4, 5, 6].includes(dow)) {
                        this.selectedDays[dow - 1] = true;
                    }
                }
                //this.validateAppointmentContract({ ...this.appointment });
            }
        },
        "appointment.startTime"() {
            if (this.appointment.startTime) {
                this.validateAppointmentContract({ ...this.appointment });
            }
        },
        "appointment.endTime"() {
            if (this.appointment.endTime) {
                this.validateAppointmentContract({ ...this.appointment });
            }
        },
        "appointment.appointment_type_id"() {
            if (this.appointment.appointment_type_id) {
                if (!this.appointmentTypeIsBillable) {
                    this.appointment.patient_id = null;
                    this.appointment.contract_id = null;
                    this.showCodes = false;
                    this.breakTime.enabled = false;
                    this.lunchTime.enabled = false;
                }
                this.validateAppointmentContract({ ...this.appointment });
            }
            const appointmentType = this.appointmentTypes.find(
                appointmentType => appointmentType.id === this.appointment.appointment_type_id
            );

            if (appointmentType && !this.appointmentTypeIsFunded(appointmentType)) {
                this.appointment.contract_id = "private_pay";
            }
        },
        showCodes() {
            if (!Array.isArray(this.appointment.diagnoses)) {
                this.appointment.diagnoses = [];
            }
        },
        contract() {
            this.recurrenceEndCustom = !(this.contract && this.contract.id);
            this.validateAppointmentContract({ ...this.appointment });
        },
        showCompleteAppointment(newVal, oldVal) {
            if (!newVal && oldVal) {
                setTimeout(this.removePickerMinWidth, 0);
            }
        },
    },

    computed: {
        appointmentTypesWithContract() {
            return this.appointmentTypes.filter(
                at => this.appointmentTypeIsFunded(at) && at.is_billable
            );
        },
        appointmentTypeIsBillable() {
            if (this.appointment.appointment_type_id) {
                const appointment = this.appointmentTypes.find(
                    a => a.id === this.appointment.appointment_type_id
                );
                if (appointment) {
                    return !!appointment.is_billable;
                }
            }
            return false;
        },
        isBeforeNow() {
            return this.moment(this.appointment.time_end_at).isBefore(this.moment().now);
        },
        canEditAppointment() {
            if (_.get(this.appointment, "id", false)) {
                return (
                    this.$parent.canEditAppointments &&
                    this.$parent.canEditSpecificAppointment(this.appointment) &&
                    (this.appointment.status !== "complete" || this.canEditCompletedAppointments)
                );
            }
            return true;
        },
        canCompleteAppointment() {
            return (
                (!this.isBeforeNow || this.canCompleteAppointmentsInThePast) && //Appointment is not in the past or user can complete appointments in the past
                ((this.appointment.status === "new" || //Appointment is new (user is the appointment's therapist)
                    helpers._get(this.therapist, "user.id", false) !== this.user.id) && //Appointment has been submitted (user is the approver)
                    this.$parent.canCompleteAppointment(this.appointment))
            );
        },
        contract() {
            return this.availableContracts && this.appointment.contract_id
                ? this.availableContracts.find(c => c.id === this.appointment.contract_id)
                : {};
        },
        isPartOfRecurringSeries() {
            return this.appointment.recurrence_series_key;
        },
        filteredFundingSources() {
            return [];
        },
        participant() {
            if (this.appointment[this.participantsListProp + "_id"]) {
                const participant = this.participantsList.find(
                    p => p.id === this.appointment[this.participantsListProp + "_id"]
                );
                const supervisedTherapist = this.organizationTherapists.find(
                    p => p.id === this.appointment.therapist_id
                );
                switch (this.participantsListProp) {
                    case "patient":
                        return participant || {};
                    case "therapist":
                        return participant || supervisedTherapist; // if participant is not in the participantsList, then it's a supervised therapist
                }
            }
        },
        patient() {
            if (this.scheduleOwnerProp === "patient") {
                return this.scheduleOwner;
            }
            return this.participant;
        },
        therapist() {
            if (this.scheduleOwner.hasOwnProperty("user")) {
                return this.scheduleOwner;
            }
            return this.participant;
        },
        appointmentType() {
            if (this.appointment.appointment_type_id) {
                const appointmentType = this.appointmentTypes.find(
                    t => t.id === this.appointment.appointment_type_id
                );
                if (appointmentType) {
                    return appointmentType;
                }
            }
            return {};
        },
        availableAddresses() {
            let addresses = [];
            [
                _.get(this.patient, "addresses", false),
                _.get(this.organization, "addresses", false),
                this.centers.map(c => c.address),
            ].forEach(a => {
                if (a) {
                    addresses = [...addresses, ...a];
                }
            });
            return addresses;
        },
        availableContracts() {
            if (this.patient) {
                let contracts = this.patient.contracts;
                if (contracts && this.appointment.appointment_type_id) {
                    contracts = contracts.filter(
                        contract =>
                            _.get(contract, "appointment_type.id", null) ===
                            this.appointment.appointment_type_id
                    );
                }
                return contracts;
            }
            return [];
        },
        selectedContractEndDate() {
            if (this.availableContracts && this.appointment.contract_id) {
                const contract = this.availableContracts.find(
                    c => c.id === this.appointment.contract_id
                );
                if (contract) return contract.end_at;
            }
            return null;
        },
        matchedParticipants() {
            if (
                this.scheduleOwnerProp &&
                this.participantsListProp &&
                this.therapistPatientRelations &&
                this.therapistPatientRelations.length
            ) {
                return this.therapistPatientRelations
                    .filter(
                        r =>
                            r[`${this.scheduleOwnerProp}_id`] ===
                            _.get(this[this.scheduleOwnerProp], "id", 0)
                    )
                    .map(r => r[`${this.participantsListProp}_id`]);
            }
            return [];
        },
        disabledDates() {
            return this.blackoutDates.map(date => this.moment(date.date).toDate()); // Date
        },
    },

    methods: {
        duskSetDate() {
            this.appointment.startDate = new Date();
            this.appointment.startTime = "8:00 AM";
            let context = this;
            setTimeout(() => {
                context.appointment.endTime = "9:00 AM";
            }, 500);
        },

        populateAddress(address) {
            let newAddress = "";
            ["address1", "address2", "city", "state", "zip"].forEach(f => {
                if (address[f]) {
                    newAddress += `${address[f]} `;
                }
            });
            newAddress.trim();
            this.$set(this.appointment, "address", newAddress);
        },

        handleChangedPatient(event) {
            // Called when user changes the patient in the select element
            this.appointment.appointment_type_id = null;
            this.appointment.contract_id = null;
        },

        appointmentTypeIsFunded(appointmentType) {
            let appointmentTypeIsFunded = false;

            if (this.patient) {
                const contracts = this.patient.contracts;

                if (contracts) {
                    contracts.forEach(contract => {
                        if (_.get(contract, "appointment_type.id", null) == appointmentType.id) {
                            appointmentTypeIsFunded = true; // Enables appointment types with contracts if patient is selected
                        }
                    });
                }
                if (["professional_gold"].includes(this.organization.type)) {
                    appointmentTypeIsFunded = true; // Gold organizations have all types enabled regardless of contracts
                }
            }

            if (this.scheduleOwnerProp === "therapist") {
                if (!appointmentType.organization_id) {
                    appointmentTypeIsFunded = true; // Allows global types if schedule owner is therapist
                }
            }

            if (!appointmentType.is_billable) {
                appointmentTypeIsFunded = true;
            }

            return appointmentTypeIsFunded;
        },

        setFrequency(value) {
            const frequency = this.frequencyOptions.find(f => f.value === value);
            this.appointment.recurrence.interval = frequency.interval;
            this.appointment.recurrence.frequency = frequency.frequency;
        },

        validateTime(time) {
            return time.match(/^([0-1]?\d|(20|21|22|23)):([0-5]\d)(\s?)([aApP][mM])?/g);
        },

        validateDate(payload) {
            if (!payload.startDate) {
                this.$swal({
                    title: "Error!",
                    text: "Appointment date needs to be set.",
                    type: "error",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
                return null;
            }
            return payload;
        },

        validatePayload(payload) {
            let validationErrors = [];
            if (!payload.appointment_type_id) {
                validationErrors.push("Appointment Type needs to be set.");
            }
            if (this.appointmentTypeIsBillable) {
                if (!payload.patient_id) {
                    validationErrors.push("Client needs to be set.");
                }
                if (payload.is_private_pay) {
                    if (!payload.charge_amount) {
                        validationErrors.push("Charge amount needs to be set for private pay.");
                    }
                }
            }
            if (!payload.therapist_id) {
                validationErrors.push("Therapist needs to be set.");
            }
            if (!payload.address) {
                validationErrors.push("Address needs to be set.");
            }
            if (
                !payload.time_start_at ||
                !payload.time_end_at ||
                !this.validateTime(payload.startTime) ||
                !this.validateTime(payload.endTime) ||
                this.moment(payload.time_end_at).format("X") <=
                    this.moment(payload.time_start_at).format("X")
            ) {
                validationErrors.push(
                    `Appointment start and end time need to be entered in the format "hh:mm AM/PM" and can not be the same.`
                );
            }
            if (
                this.recurring &&
                (!payload.recurrence ||
                    !payload.recurrence.frequency ||
                    !payload.recurrence.interval)
            ) {
                validationErrors.push("Appointment repetition needs to be set.");
            }
            if (
                this.recurring &&
                !payload.recurrence.days_of_week.includes(
                    this.moment(payload.time_start_at)
                        .format("dd")
                        .toUpperCase()
                )
            ) {
                validationErrors.push(
                    "At least the appointment's day of the week need to be selected in the repetition pattern."
                );
            }
            if (this.recurring && (this.recurrenceEndCustom && !payload.recurrence.end_at)) {
                validationErrors.push("Recurrence end date needs to be set.");
            }
            if (
                this.recurring &&
                (this.recurrenceEndCustom &&
                    this.moment(payload.recurrence.end_at).isBefore(this.moment().now))
            ) {
                validationErrors.push("Recurrence end date cannot be in the past.");
            }
            if (
                this.breakTime.enabled &&
                (!this.breakTime.startTime ||
                    !this.validateTime(this.breakTime.startTime) ||
                    !this.breakTime.duration)
            ) {
                validationErrors.push(
                    `Break time and duration need to be set to after the appointment's start and be entered in the format "hh:mm AM/PM".`
                );
            }
            if (
                this.lunchTime.enabled &&
                (!this.lunchTime.startTime ||
                    !this.validateTime(this.lunchTime.startTime) ||
                    !this.lunchTime.duration)
            ) {
                validationErrors.push(
                    `Lunch time and duration need to be set to after the appointment's start and be entered in the format "hh:mm AM/PM".`
                );
            }
            if (validationErrors.length) {
                this.$swal({
                    title: "Error",
                    html: validationErrors.join(" <br>"),
                    type: "error",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
            }
            return validationErrors.length === 0;
        },

        formatTimeValues(payload) {
            if (payload.startDate && payload.startTime && payload.endTime) {
                let startTime = this.moment(payload.startTime, "hh:mm A");
                let endTime = this.moment(payload.endTime, "hh:mm A");
                payload.time_start_at = this.moment(payload.startDate)
                    .hour(startTime.hour())
                    .minute(startTime.minute())
                    .format("YYYY-MM-DD HH:mm:ss");
                payload.time_end_at = this.moment(payload.startDate)
                    .hour(endTime.hour())
                    .minute(endTime.minute())
                    .format("YYYY-MM-DD HH:mm:ss");

                if (this.breakTime.startTime) {
                    let breakStartTime = this.moment(this.breakTime.startTime, "hh:mm A");
                    payload.break_time_start_at = this.moment(payload.startDate)
                        .hour(breakStartTime.hour())
                        .minute(breakStartTime.minute())
                        .format("YYYY-MM-DD HH:mm:ss");
                    payload.break_time_end_at = this.moment(payload.startDate)
                        .hour(breakStartTime.hour())
                        .minute(breakStartTime.minute())
                        .add(this.breakTime.duration, "minutes")
                        .format("YYYY-MM-DD HH:mm:ss");
                }

                if (this.lunchTime.startTime) {
                    let lunchStartTime = this.moment(this.lunchTime.startTime, "hh:mm A");
                    payload.lunch_time_start_at = this.moment(payload.startDate)
                        .hour(lunchStartTime.hour())
                        .minute(lunchStartTime.minute())
                        .format("YYYY-MM-DD HH:mm:ss");
                    payload.lunch_time_end_at = this.moment(payload.startDate)
                        .hour(lunchStartTime.hour())
                        .minute(lunchStartTime.minute())
                        .add(this.lunchTime.duration, "minutes")
                        .format("YYYY-MM-DD HH:mm:ss");
                }
            }
            return payload;
        },

        formatRecurrence(payload) {
            if (!payload.hasOwnProperty("recurrence") || !_.get(payload, "recurrence.end_at", null))
                return payload;

            const weekdays = ["MO", "TU", "WE", "TH", "FR", "SA"];
            payload.recurrence.days_of_week = [];
            this.selectedDays.forEach((d, i) => {
                if (d) {
                    payload.recurrence.days_of_week.push(weekdays[i]);
                }
            });
            if (payload.recurrence.days_of_week.length) {
                payload.recurrence.days_of_week = Array.from(
                    new Set(payload.recurrence.days_of_week)
                );
                switch (this.frequency) {
                    case "WEEKLY":
                        payload.recurrence.interval = 1;
                        payload.recurrence.frequency = "WEEKLY";
                        break;
                    case "WEEKLY2":
                        payload.recurrence.interval = 2;
                        payload.recurrence.frequency = "WEEKLY";
                        break;
                    case "MONTHLY":
                        payload.recurrence.interval = 1;
                        break;
                }
            } else {
                delete payload.recurrence;
            }
            if (payload.recurrence && !payload.recurrence.end_at) {
                delete payload.recurrence.end_at;
            }
            delete payload.recurrence_series_key;
            return payload;
        },

        prepareSavePayload(payload) {
            if (this.recurring) {
                if (!this.recurrenceEndCustom && this.selectedContractEndDate) {
                    payload.recurrence.end_at = this.moment(this.selectedContractEndDate)
                        .hour(23)
                        .minute(59)
                        .second(59)
                        .format("YYYY-MM-DD HH:mm:ss");
                } else {
                    payload.recurrence.end_at = this.appointment.recurrence.end_at
                        ? this.moment(this.appointment.recurrence.end_at)
                              .hour(23)
                              .minute(59)
                              .second(59)
                              .format("YYYY-MM-DD HH:mm:ss")
                        : null;
                }
            } else {
                delete payload.recurrence;
            }
            if (this.breakTime.enabled) {
                payload.breakTime = this.breakTime;
            }
            if (this.lunchTime.enabled) {
                payload.lunchTime = this.lunchTime;
            }
            payload = this.formatTimeValues(payload);
            payload = this.formatRecurrence(payload);
            payload = helpers.prepareCodesPayload(payload);

            if (payload.contract_id === "private_pay") {
                delete payload.contract_id;
                payload.is_private_pay = 1;
            }

            if (payload.status !== "complete") {
                payload.status = "new";
            }

            payload[this.scheduleOwnerProp + "_id"] = this.scheduleOwner.id;
            delete payload.appointment_template_id; //TODO: temporary

            return payload;
        },

        parseEditPayload(payload) {
            // Payload passes first through $parent.prepareEditPayload()
            this.appointment = payload;

            if (
                !this.appointmentTypes
                    .map(apt => apt.id)
                    .includes(this.appointment.appointment_type_id)
            ) {
                this.appointment.appointment_type_id = null;
            } // sets to null if appointment type does not exist anymore

            // legacy data will not have a preferred_cpt_code_id
            // default to one for backwards compatibility
            if (!payload.preferred_cpt_code_id && (payload.cpt_codes && payload.cpt_codes.length)) {
                let code = _.get(this.contract, "cpt_codes", []).find(function(code) {
                    return code.code == payload.cpt_codes[0];
                });

                if (code) {
                    this.appointment.preferred_cpt_code_id = code.id;
                }
            }

            if (
                this.appointment.preferred_cpt_code_id ||
                (payload.service_codes && payload.service_codes.length) ||
                (payload.diagnoses && payload.diagnoses.length)
            ) {
                this.showCodes = true;
            }

            if (payload.is_private_pay) {
                payload.contract_id = "private_pay";
            }

            if (payload.breakTime) {
                this.breakTime = payload.breakTime;
                this.breakTimeClean = { ...payload.breakTime };
            } else {
                this.breakTimeClean = { duration: null, enabled: false, startTime: null };
            }
            if (payload.lunchTime) {
                this.lunchTime = payload.lunchTime;
                this.lunchTimeClean = { ...payload.lunchTime };
            } else {
                this.lunchTimeClean = { duration: null, enabled: false, startTime: null };
            }

            if (payload.recurrence) {
                //this.recurrenceClean = JSON.stringify(payload.recurrence); // Copy original data to check for changes
                // IMPORTANT: the recurrenceClean property used for change detection is being set by the parent component

                this.recurring = !!payload.recurrence.frequency;

                if (payload.recurrence.interval === 2) {
                    this.appointment.recurrence.frequency = "WEEKLY2";
                } else {
                    if (payload.recurrence.interval === "WEEKLY") {
                        this.appointment.recurrence.frequency = "WEEKLY";
                    } else {
                        this.appointment.recurrence.frequency = "MONTHLY";
                    }
                }

                this.recurrenceEndCustom = true;
            } else {
                this.recurring = false;
            }

            this.endTime = this.appointment.endTime;
            this.appointment.endTime = "";

            setTimeout(() => {
                this.fixEndTimeAfterLoad();
                this.appointmentClean = { ...this.appointment }; // Copy original data to check for changes
            }, 1); // End time changes when entries from the time picker
        },

        fixEndTimeAfterLoad() {
            this.$set(this.appointment, "endTime", this.appointment.endTimeFixed);
        },

        async validateAppointmentContract(payload) {
            this.contractValidationMessage = "";

            if (this.organization.type !== "professional_platinum") return;
            if (
                !payload.contract_id ||
                !payload.appointment_type_id ||
                !(this.contract && this.contract.approved_unit_type)
            )
                return; // No contract to check, yet

            let _payload = { ...payload };

            switch (this.contract.approved_unit_type) {
                case "session":
                    // Exceeds total
                    if (
                        parseInt(this.contract.approved_used) >
                        parseInt(this.contract.approved_max_total_calculated)
                    ) {
                        this.contractValidationMessage =
                            "This appointment will exceed the max allowable session quota for appointments";
                        return false;
                    }
                    if (!_payload.startDate) return true; // Can't calculate per period

                    if (!_payload.time_start_at) {
                        if (this.moment(_payload.startDate)._isValid) {
                            _payload.time_start_at = this.moment(_payload.startDate).format(
                                "YYYY-MM-DD HH:mm:ss"
                            );
                        }
                    }

                    break;

                case "minute":
                case "amount":
                    if (!_payload.startDate || !_payload.startTime || !_payload.endTime)
                        return true; // No date or duration, can't calculate

                    try {
                        _payload.time_start_at = this.moment(_payload.startDate)
                            .hour(_payload.startTime.split(":")[0])
                            .minute(_payload.startTime.split(":")[1].split(" ")[0])
                            .format("YYYY-MM-DD HH:mm:ss");
                        _payload.time_end_at = this.moment(_payload.startDate)
                            .hour(_payload.endTime.split(":")[0])
                            .minute(_payload.endTime.split(":")[1].split(" ")[0])
                            .format("YYYY-MM-DD HH:mm:ss");
                    } catch (e) {
                        return true; // Skips calculation because startTime or endTime couldn't be parsed -- TypeError
                    }

                    if (
                        _payload.time_start_at === "Invalid date" ||
                        _payload.time_end_at === "Invalid date"
                    ) {
                        return true; // Skips calculation if date couldn't be parsed
                    }
            }
            let res = await axios.post(
                `/api/organizations/${this.organization.id}/scheduling/appointments/validate`,
                { appointment: _payload }
            );
            this.contractValidationMessage = _.get(res, "data.message", null);
            return _.get(res, "data.status", null) !== "failure";
        },

        saveAppointment(removeRecurrence) {
            let payload = { ...this.appointment };
            if (!this.validateDate(payload)) return;
            payload = this.prepareSavePayload(payload);
            if (removeRecurrence) {
                this.recurring = false;
                delete payload.recurrence;
            }
            if (!this.validatePayload(payload)) return;

            payload = helpers.cleanEmptyProperties(payload);
            axios({
                method: payload.id ? "PATCH" : "POST",
                url: payload.id
                    ? `/api/organizations/${this.organization.id}/scheduling/appointments/${
                          payload.id
                      }`
                    : `/api/organizations/${this.organization.id}/scheduling/appointments`,
                data: payload,
            })
                .then(r => {
                    this.clearAppointment();
                    this.$emit("appointment-created");
                })
                .catch(error => {
                    this.$swal({
                        title: "Error!",
                        text:
                            "The appointment could not be saved. Please contact support if this error persists.",
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    console.error(error);
                });
        },

        handleCompleteAppointment() {
            if (this.appointmentIsDirty) {
                this.$swal({
                    text: "Please save your changes before completing the appointment.",
                    type: "warning",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
            }
            return this.completeAppointment();
        },

        confirmDeleteAppointment() {
            this.appointmentCancellation.cancel = true;
        },

        deleteAppointment(payload) {
            this.setErrors();
            let url = `/api/organizations/${this.organization.id}/scheduling/appointments/${
                this.appointment.id
            }`;

            if (this.appointmentCancellation.reason === "other") {
                this.appointmentCancellation.reason = this.appointmentCancellation.specify;
            }
            const deletePayload = {
                notes:
                    (this.appointment.notes || "") +
                    (this.appointment.notes ? " " : "") +
                    this.appointmentCancellation.reason,
            };

            payload = { ...payload, ...deletePayload };

            if (payload) {
                let query = "?";
                Object.keys(payload).forEach(k => {
                    query += k + "=" + payload[k] + "&";
                });
                url = url + query;
            }

            axios
                .delete(url, deletePayload)
                .then(r => {
                    if (_.get(r, "data.status", null) === "failed") {
                        this.setErrors({
                            Error: [
                                _.get(
                                    r,
                                    "data.message",
                                    "An error occurred while attempting to delete the appointment."
                                ),
                            ],
                        });
                    } else {
                        this.clearAppointment();
                        this.$emit("appointment-created");
                    }
                })
                .catch(error => {
                    this.setErrors(_.get(error, "response.data.errors", {}));
                    console.error(error);
                });
        },

        closeCompleteAppointment() {
            this.showCompleteAppointment = false;
            this.$emit("appointment-created");
        },

        completeAppointment() {
            if (
                !(this.appointment.contract_id || !this.appointmentTypeIsBillable) &&
                !["professional_gold"].includes(this.organization.type)
            )
                return;
            this.showCompleteAppointment = true;
            this.$emit("set-title", "Complete Session");
        },

        approveAppointment() {
            if (
                this.appointment.id &&
                this.appointment.billing_therapist_id &&
                this.appointment.therapist_id !== _.get(this.currentUserTherapist, "id", null)
            ) {
                this.showCompleteAppointment = true;
                this.$emit("set-title", "Approve Session");
            }
        },

        clearAppointment() {
            this.appointmentCancellation = {
                cancel: false,
                reason: null,
                specify: "",
            };
            this.appointment = {
                appointment_type_id: null,
                patient_id: null,
                startTime: null,
                endTime: null,
                therapist_id: null,
                address_id: null,
                contract_id: null,
                charge_amount: null,
                is_private_pay: 0,
                time_start_at: null,
                time_end_at: null,
                diagnoses: [],
                cpt_codes: null,
                service_codes: null,
                preferred_cpt_code_id: null,
                service_location_id: null,
                recurrence: {
                    days_of_week: [],
                    frequency: null,
                    interval: null,
                },
            };
            this.recurring = true;
            this.breakTime = {
                enabled: false,
                startTime: null,
                duration: null,
            };
            this.lunchTime = {
                enabled: false,
                startTime: null,
                duration: null,
            };
            this.showCodes = false;
            this.selectedDays = [false, false, false, false, false, false];
            this.recurrenceEndCustom = true;
        },

        prettyType(type) {
            let spaced = type.replace(/_/g, " ");
            spaced = spaced.split(" ");
            for (let i = 0; i < spaced.length; i++) {
                spaced[i] = spaced[i][0].toUpperCase() + spaced[i].substr(1);
            }
            return spaced.join(" ");
        },
    },
};
</script>

<style scoped lang="scss">
.selected-day {
    transition: all 0.4s;
    margin-right: 0.5rem;
    min-width: 2rem;
    max-width: 2rem;
    min-height: 2rem;
    max-height: 2rem;
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: solid 2px #38ba48;
    color: #38ba48;
}
.unselected-day {
    transition: all 0.4s;
    margin-right: 0.5rem;
    min-width: 2rem;
    max-width: 2rem;
    min-height: 2rem;
    max-height: 2rem;
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: solid 2px #8f8f8f;
    color: #8f8f8f;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
}
</style>
